import {type ReactNode} from 'react'
import classnames from 'classnames'
import {Link, useLocation} from 'react-router-dom'

import {TooltipWrapper, TooltipBody} from 'components'

export type AccessDataToggleProps = {
  /** Path for first link */
  firstLink: string
  /** Path for second link */
  secondLink: string
  /** Text for first link */
  firstText: string
  /** Text for second link */
  secondText: string
  /** Delimiter element between links (defaults to ' / ') */
  delimiter?: ReactNode
  /** Whether the first link is disabled */
  isFirstLinkDisabled?: boolean
  /** Whether the second link is disabled */
  isSecondLinkDisabled?: boolean
  /** Wrapper class name */
  wrapperClassName?: string
}

export const AccessDataToggle = ({
  firstLink,
  secondLink,
  firstText,
  secondText,
  delimiter = ' / ',
  wrapperClassName = '',
  isFirstLinkDisabled = false,
  isSecondLinkDisabled = false,
}: AccessDataToggleProps) => {
  const {pathname} = useLocation()
  const firstIsActive = pathname === firstLink

  const handleDisableLink = (
    event: React.MouseEvent<HTMLAnchorElement>,
  ): void => {
    event.preventDefault()
  }

  return (
    <div data-cy="access-data-toggle" className={wrapperClassName}>
      <Link
        to={firstLink}
        className={classnames('abc-regular inline-block', {
          'text-sm opacity-50 cursor-pointer': !firstIsActive,
          'text-2xl cursor-default': firstIsActive,
          'hover:opacity-100 transition-all duration-150 hover:scale-105':
            !firstIsActive && !isFirstLinkDisabled,
        })}
        data-cy="access-data-toggle-first-link"
        aria-label="Access Data Toggle First Link"
        {...(isFirstLinkDisabled && {
          tabIndex: -1,
          onClick: handleDisableLink,
          'data-tooltip-id': 'access-data-toggle-title-tooltip',
        })}
      >
        {firstText}
      </Link>
      <span className="text-base">{delimiter}</span>
      <Link
        to={secondLink}
        className={classnames('abc-regular inline-block', {
          'text-sm opacity-50 cursor-pointer': firstIsActive,
          'text-2xl cursor-default': !firstIsActive,
          'hover:opacity-100 transition-all duration-150 hover:scale-105':
            firstIsActive && !isSecondLinkDisabled,
        })}
        data-cy="access-data-toggle-second-link"
        aria-label="Access Data Toggle Second Link"
        {...(isSecondLinkDisabled && {
          tabIndex: -1,
          onClick: handleDisableLink,
        })}
      >
        {secondText}
      </Link>
      <TooltipWrapper
        id="access-data-toggle-title-tooltip"
        noArrow={true}
        place="left"
        className="!p-0"
      >
        <TooltipBody>Coming Soon</TooltipBody>
      </TooltipWrapper>
    </div>
  )
}
